import useTranslation from 'next-translate/useTranslation';
import { useCallback, useEffect, useRef, useState } from 'react';
import flashSaleAssets from '@/images/flash-sale/index';
import { ProductVariant } from '@/models/Product';
import { formatFlashSaleTime, getVariantPricing } from '@/utils/helpers/flashSale';
import formatCurrency from '@/utils/helpers/formatCurrency';
import { refreshFlashSalePricing } from '@/utils/helpers/refreshFlashSalePricing';
import FlashSaleAlternatingInfo from './FlashSaleAlternatingInfo';
import FlashSalePriceBadge from './FlashSalePriceBadge';
const MRP_COLOR = '#80868C';
const OFF_COLOR = '#43A314';
interface Props {
  variant: ProductVariant;
  onExpire?: () => void;
  compact?: boolean;
}
const FlashSalePrice = ({
  variant,
  onExpire,
  compact = false
}: Props) => {
  const {
    t
  } = useTranslation('products');
  const pricing = getVariantPricing(variant);
  const hasExpiredRef = useRef(false);
  const [remainingSeconds, setRemainingSeconds] = useState(pricing.activeDiscount?.time_remaining_seconds ?? 0);
  const handleExpire = useCallback(() => {
    onExpire?.() ?? refreshFlashSalePricing();
  }, [onExpire]);
  useEffect(() => {
    if (!pricing.isFlashSale || !pricing.activeDiscount) {
      return;
    }
    const initialSeconds = pricing.activeDiscount.time_remaining_seconds ?? 0;
    setRemainingSeconds(initialSeconds);
    hasExpiredRef.current = false;
    if (initialSeconds <= 0) {
      handleExpire();
      return;
    }
    const interval = setInterval(() => {
      setRemainingSeconds(prev => {
        const next = Math.max(prev - 1, 0);
        if (next === 0 && !hasExpiredRef.current) {
          hasExpiredRef.current = true;
          handleExpire();
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [pricing.isFlashSale, pricing.activeDiscount, pricing.activeDiscount?.time_remaining_seconds, handleExpire]);
  if (!pricing.isFlashSale || !pricing.activeDiscount) {
    return null;
  }
  const timeFormat = formatFlashSaleTime(remainingSeconds);
  const timerLabel = timeFormat.unit === 'days' ? t('price_drop_for_days', {
    days: timeFormat.days
  }) : timeFormat.unit === 'minutes' ? t('price_drop_for_minutes', {
    minutes: timeFormat.minutes
  }) : t('price_drop_for', {
    hours: timeFormat.hours,
    minutes: timeFormat.minutes
  });
  const discountAmount = Math.round(pricing.activeDiscount.discount_amount);
  return <div className="flex flex-col gap-1 w-full min-w-0" data-sentry-component="FlashSalePrice" data-sentry-source-file="FlashSalePrice.tsx">
      <div className={`flex flex-wrap items-center min-h-5 min-w-0 ${compact ? 'gap-x-1 gap-y-0.5' : 'gap-x-1.5 gap-y-0.5'}`}>
        <FlashSalePriceBadge label={formatCurrency(pricing.salePrice)?.replace(/\.00$/, '') ?? ''} data-sentry-element="FlashSalePriceBadge" data-sentry-source-file="FlashSalePrice.tsx" />
        <span className="inline-flex items-center h-5 text-sm leading-none line-through truncate" style={{
        color: MRP_COLOR
      }}>
          {formatCurrency(pricing.mrp)?.replace(/\.00$/, '')}
        </span>
        <span className="inline-flex items-center h-5 text-xs leading-none truncate" style={{
        color: OFF_COLOR
      }}>
          {pricing.discountPercent}% {t('sale_off')}
        </span>
      </div>
      {!compact ? <FlashSaleAlternatingInfo items={[{
      icon: flashSaleAssets.AlarmClock,
      label: timerLabel
    }, {
      icon: flashSaleAssets.Lightning,
      label: t('amount_off', {
        amount: formatCurrency(discountAmount)?.replace(/\.00$/, '')
      })
    }]} /> : null}
    </div>;
};
export default FlashSalePrice;